import { c as _c } from "react/compiler-runtime";
import { SectionHeadline } from '@/components/SectionHeadline';
import { Link, type LinkProps } from '@/components/links';
import React, { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ViewGridIcon from '@/assets/icons/heroIcons/View-grid.svg';
import ShoppingCartIcon from '@/assets/icons/heroIcons/Shopping-cart.svg';
import SpeakerphoneIcon from '@/assets/icons/heroIcons/Speakerphone.svg';
import CreditCardIcon from '@/assets/icons/heroIcons/Credit-card.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconMobile from '@/assets/icons/heroIcons/Device-mobile.svg';
import IconArrow from '@/assets/icons/heroIcons/Arrow-right.svg';
import { EXTERNAL_LINKS, LINKS } from '@/constants/links';
import { mergeClasses } from '@/utils/style';
import { motion } from 'framer-motion';
import RevolvingIcon from '@/assets/icons/heroIcons/Revolving.svg';
import DeferredPaymentIcon from '@/assets/icons/heroIcons/Deferred-payment.svg';
import ThirdIcon from '@/assets/icons/heroIcons/Third.svg';
import Image from 'next/image';
import { Button, PlayButton } from '@/components/buttons';
import { Text } from '@/components/typography';
import { VideoModal } from '@/components/modal';
import { Headline } from '@/components/typography';
import { useElementTopPosition } from '@/hooks/useElementTopPosition';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getMediaQuery } from '@/styles/sizes';
import ChevronDownIcon from '@/assets/icons/heroIcons/Chevron-down.svg';
import { ClientGate } from '@/components/ClientGate';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
import dynamic from 'next/dynamic';
const PointingMascots = dynamic(() => import('@/components/animation/PointingMascots').then(mod => mod.PointingMascots), {
  ssr: false
});
interface SectionProps {
  icon: ReactNode;
  title: string | ReactNode;
  description: ReactNode;
  link: LinkProps;
  isOpen: boolean;
  onClick: () => void;
}
function ClientSection(props) {
  const $ = _c(12);
  const {
    icon,
    title,
    isOpen
  } = props;
  const t0 = isOpen ? 180 : 0;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      rotate: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isOpen ? 180 : 0;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      rotate: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronDownIcon />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t3) {
    t5 = <motion.div className="ml-auto flex transition-transform md:hidden" style={t1} initial={t3}>{t4}</motion.div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== icon || $[9] !== t5 || $[10] !== title) {
    t6 = <div className="flex items-center gap-4 text-lg font-bold lg:gap-2 lg:text-2xl">{icon}{title}{t5}</div>;
    $[8] = icon;
    $[9] = t5;
    $[10] = title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function Section(props) {
  const $ = _c(24);
  const {
    description,
    icon,
    link,
    title,
    isOpen,
    onClick
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["homepage"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getMediaQuery("lg");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isDesktop = useMediaQuery(t1);
  let t2;
  if ($[2] !== description || $[3] !== icon || $[4] !== isDesktop || $[5] !== isOpen || $[6] !== link || $[7] !== t || $[8] !== title) {
    t2 = function renderContent() {
      return <><div className="flex items-center gap-4 lg:gap-2">{icon}<Headline as="h4" variant="h5">{title}</Headline><motion.div className="ml-auto flex transition-transform md:hidden" style={{
            rotate: isOpen ? 180 : 0
          }} initial={{
            rotate: isOpen ? 180 : 0
          }}><ChevronDownIcon /></motion.div></div>{isDesktop ? <Text className="text-light-gray-100 mt-5 pb-16" variant="p3" as="div">{description}</Text> : <>{isOpen && <><div className="mt-5 pb-16 text-base">{description}</div><Link {...link} className="text-white">{t("discover.card.showMore")}</Link></>}</>}<div className="absolute right-0 bottom-0 hidden h-18 w-18 items-center justify-center rounded-tl-4xl rounded-br-4xl bg-blue-700 text-white md:flex"><IconArrow /></div></>;
    };
    $[2] = description;
    $[3] = icon;
    $[4] = isDesktop;
    $[5] = isOpen;
    $[6] = link;
    $[7] = t;
    $[8] = title;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const renderContent = t2;
  if (isDesktop) {
    let t3;
    if ($[10] !== renderContent) {
      t3 = <div className="relative h-full rounded-2xl bg-blue-500 p-6 text-white transition hover:bg-blue-700 md:rounded-4xl md:p-8 lg:p-6">{renderContent()}</div>;
      $[10] = renderContent;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    let t4;
    if ($[12] !== link || $[13] !== t3) {
      t4 = <Link {...link} className="no-underline">{t3}</Link>;
      $[12] = link;
      $[13] = t3;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    return t4;
  }
  const t3 = isOpen ? "bg-blue-800" : "bg-blue-500";
  let t4;
  if ($[15] !== t3) {
    t4 = mergeClasses("relative h-full w-full rounded-2xl p-4 text-left text-white transition sm:p-6 md:rounded-4xl md:p-8", t3);
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== renderContent) {
    t5 = renderContent();
    $[17] = renderContent;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== isOpen || $[20] !== onClick || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className="relative"><button type="button" className={t4} onClick={onClick} aria-expanded={isOpen}>{t5}</button></div>;
    $[19] = isOpen;
    $[20] = onClick;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}
function TopSection() {
  const $ = _c(23);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["homepage"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const [isVisible, setIsVisible] = useState(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = mergeClasses("relative col-auto overflow-hidden rounded-2xl text-white sm:rounded-4xl", "col-span-2");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("discover.topSection");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <Image src="/images/discover/discover.png" width="768" height="384" alt={t2} className="h-42 w-full object-cover sm:h-96 lg:h-full" />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("discover.topSection");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <Headline as="h5" variant="h5" className="absolute top-6 left-6 max-w-[10rem] whitespace-pre-line text-white sm:top-auto sm:bottom-11 sm:left-11 sm:max-w-[15rem]">{t4}</Headline>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsVisible(true);
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("discover.topSection.button");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <PlayButton analyticsParam="discoverVideo" testId="discoverVideo" variant="primary" onClick={t6} className="absolute right-4 bottom-4 sm:right-10 sm:bottom-10">{t7}</PlayButton>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setIsVisible(false);
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== isVisible) {
    t10 = <VideoModal isOpened={isVisible} autoplay={true} videoId="Uv02-8EyygE" handleClose={t9} />;
    $[16] = isVisible;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t3 || $[20] !== t5 || $[21] !== t8) {
    t11 = <div className={t1}>{t3}{t5}{t8}{t10}</div>;
    $[18] = t10;
    $[19] = t3;
    $[20] = t5;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}
function ProductItem(props) {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.title) {
    t0 = <p className="font-semibold text-white">{props.title}</p>;
    $[0] = props.title;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.icon || $[3] !== t0) {
    t1 = <li className="flex gap-2">{props.icon}{t0}</li>;
    $[2] = props.icon;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
export function Discover() {
  const $ = _c(47);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["homepage"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const [ref, t1] = useElementTopPosition();
  const {
    absoluteTop
  } = t1;
  const isCsLanguage = useIsSelectedLanguage("cs");
  const [openedItem, setOpenedItem] = useState(null);
  let t2;
  if ($[1] !== openedItem) {
    t2 = function handleSectionClick(id) {
      setOpenedItem(openedItem === id ? null : id);
    };
    $[1] = openedItem;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleSectionClick = t2;
  let t3;
  if ($[3] !== isCsLanguage || $[4] !== t) {
    t3 = isCsLanguage ? [{
      id: "products",
      title: <span className="whitespace-nowrap">{t("discover.products.title")}</span>,
      description: <div><p>{t("discover.products.description")}</p><ul className="mt-5 flex flex-col gap-3"><ProductItem icon={<RevolvingIcon className="h-7 w-7" />} title={t("discover.products.maxi")} /><ProductItem icon={<RevolvingIcon className="h-7 w-7" />} title={t("discover.products.mini")} /><ProductItem icon={<DeferredPaymentIcon className="h-7 w-7" />} title={t("discover.products.deferredPayment")} /><ProductItem icon={<ThirdIcon className="h-7 w-7" />} title={t("discover.products.payInThree")} /></ul></div>,
      icon: <ViewGridIcon className="h-10 min-h-10 w-10 min-w-10 sm:h-12 sm:w-12" />,
      link: {
        analyticsParam: "discoverProducts",
        testId: "discoverProducts",
        to: LINKS().allProducts
      }
    }, {
      id: "whereToShop",
      title: t("discover.whereToShop.title"),
      description: t("discover.whereToShop.description"),
      icon: <ShoppingCartIcon className="h-10 min-h-10 w-10 min-w-10 sm:h-12 sm:w-12" />,
      link: {
        analyticsParam: "discoverWhereToShop",
        testId: "discoverWhereToShop",
        to: LINKS().whereToShop
      }
    }, {
      id: "rewardsAndDiscounts",
      title: t("discover.rewardsAndDiscounts.title"),
      description: t("discover.rewardsAndDiscounts.description"),
      icon: <SpeakerphoneIcon className="h-10 min-h-10 w-10 min-w-10 sm:h-12 sm:w-12" />,
      link: {
        analyticsParam: "discoverRewardsAndDiscounts",
        testId: "discoverRewardsAndDiscounts",
        to: EXTERNAL_LINKS().marketingPromos
      }
    }, {
      id: "card",
      title: t("discover.card.title"),
      description: t("discover.card.description"),
      icon: <CreditCardIcon className="h-10 min-h-10 w-10 min-w-10 sm:h-12 sm:w-12" />,
      link: {
        analyticsParam: "discoverCard",
        testId: "discoverCard",
        to: LINKS().card
      }
    }] : [{
      id: "payInThree",
      title: t("discover.payInThree.title"),
      description: t("discover.payInThree.description"),
      icon: <IconThird className="h-10 min-h-10 w-10 min-w-10 sm:h-12 sm:w-12" />,
      link: {
        analyticsParam: "discoverPayInThree",
        testId: "discoverPayInThree",
        to: LINKS().payInThree
      }
    }, {
      id: "whereToShop",
      title: t("discover.whereToShop.title"),
      description: t("discover.whereToShop.description"),
      icon: <ShoppingCartIcon className="h-10 min-h-10 w-10 min-w-10 sm:h-12 sm:w-12" />,
      link: {
        analyticsParam: "discoverWhereToShop",
        testId: "discoverWhereToShop",
        to: LINKS().whereToShop
      }
    }, {
      id: "mobileApp",
      title: t("discover.mobileApp.title"),
      description: t("discover.mobileApp.description"),
      icon: <IconMobile className="h-10 min-h-10 w-10 min-w-10 sm:h-12 sm:w-12" />,
      link: {
        analyticsParam: "discoverMobileApp",
        testId: "discoverMobileApp",
        to: LINKS().mobileApp
      }
    }];
    $[3] = isCsLanguage;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const sectionsData = t3;
  let t4;
  if ($[6] !== t) {
    t4 = t("discover.headline");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <SectionHeadline variant="inverted" anchorKey="discover.headline">{t4}</SectionHeadline>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("discover.description");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <Text as="p" variant="p2" className="text-light-gray-100 mx-auto mt-5 max-w-192 text-center lg:whitespace-pre-wrap">{t6}</Text>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t7) {
    t8 = <div className="px-4">{t5}{t7}</div>;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== absoluteTop) {
    t9 = <PointingMascots absoluteTop={absoluteTop} brainClassName="absolute right-0 top-64 w-42 will-change-transform sm:top-42 sm:w-56 md:w-80 xl:top-0 4xl:w-auto" heartClassName="absolute left-0 top-64 w-48 will-change-transform sm:top-42 sm:w-56 md:w-80 xl:top-0 4xl:w-auto" />;
    $[17] = absoluteTop;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== isCsLanguage) {
    t10 = isCsLanguage && <TopSection />;
    $[19] = isCsLanguage;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== openedItem || $[22] !== sectionsData) {
    t11 = sectionsData.map(section => <ClientSection isOpen={openedItem === section.id} key={section.id} {...section} />);
    $[21] = openedItem;
    $[22] = sectionsData;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== handleSectionClick || $[25] !== openedItem || $[26] !== sectionsData) {
    t12 = sectionsData.map(section_0 => <Section key={section_0.id} isOpen={openedItem === section_0.id} onClick={() => handleSectionClick(section_0.id)} {...section_0} />);
    $[24] = handleSectionClick;
    $[25] = openedItem;
    $[26] = sectionsData;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <ClientGate fallback={t11}>{t12}</ClientGate>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t13) {
    t14 = <div className="mt-48 flex flex-col gap-2 px-4 md:grid md:grid-cols-2 md:gap-8 md:px-12 lg:grid-cols-3 lg:gap-8 lg:px-14 xl:mt-20 xl:px-0">{t10}{t13}</div>;
    $[31] = t10;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14 || $[35] !== t8 || $[36] !== t9) {
    t15 = <div className="m-auto w-full max-w-292">{t8}{t9}{t14}</div>;
    $[34] = t14;
    $[35] = t8;
    $[36] = t9;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = LINKS();
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = t16.registration;
  let t18;
  if ($[39] !== t) {
    t18 = t("discover.try.button");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18) {
    t19 = <Button analyticsParam="discoverTry" testId="discoverTry" variant="primary" className="mx-auto mt-12 mb-2 lg:mt-16 lg:w-72" to={t17}>{t18}</Button>;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== ref || $[44] !== t15 || $[45] !== t19) {
    t20 = <><div ref={ref} className="relative z-0">{t15}{t19}</div></>;
    $[43] = ref;
    $[44] = t15;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  return t20;
}